/**
 * Grand Quack Auto — duckling entity.
 *
 * One ducking spawned per quack (cap DUCKLING.maxBrood). Visual is the cached
 * duckling.glb model; falls back to a yellow sphere if the model hasn't loaded
 * by the time the first quack lands.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DUCKLING, PALETTE } from '../config';
import type { CopEntity } from '../systems/copSystem';
import type { World } from '../types';

export type DucklingMode = 'following' | 'defending' | 'returning';

export interface DucklingEntity {
  group: THREE.Group;
  mode: DucklingMode;
  /** Index in the brood — used to read its slot from the trail buffer. */
  index: number;
  /** Cop entity being defended-against (when mode === 'defending'). */
  assignedCop: CopEntity | null;
  /** Phase angle around the cop while orbiting. */
  orbitAngle: number;
  /** When mode flips to 'returning', the time it should switch back to following. */
  returnUntil: number;
}

// Cache the GLTF un-normalized; normalize per-clone to keep math local.
let cachedRawTemplate: THREE.Group | null = null;
let templateLoading = false;
const templateReadyCallbacks: Array<() => void> = [];

function loadTemplate(): void {
  if (templateLoading || cachedRawTemplate) return;
  templateLoading = true;
  new GLTFLoader().load('/models/duckling.glb', (gltf) => {
    const model = gltf.scene;
    model.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      const oldMat = mesh.material as THREE.MeshStandardMaterial;
      mesh.material = new THREE.MeshToonMaterial({
        color: oldMat?.color ?? PALETTE.duck,
        map: oldMat?.map ?? null,
      });
    });
    cachedRawTemplate = model;
    // Retroactively upgrade any ducklings that hatched with the sphere fallback.
    for (const cb of templateReadyCallbacks) cb();
    templateReadyCallbacks.length = 0;
  });
}

// Eager preload — kicks off the GLTF fetch as soon as this module is imported,
// well before the splash + countdown finish. Avoids fallback in the common case.
loadTemplate();

/** Always returns a visual ~1 unit tall, anchored with its base at y=0. */
function buildVisual(): THREE.Object3D {
  if (cachedRawTemplate) {
    const clone = cachedRawTemplate.clone(true);
    // Per-clone material so nothing the evolution system (or any other code)
    // does to the cached template's materials can leak into our brood.
    clone.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      const oldMat = mesh.material as THREE.MeshToonMaterial;
      mesh.material = new THREE.MeshToonMaterial({
        color: oldMat?.color ?? PALETTE.duck,
        map: oldMat?.map ?? null,
      });
    });
    const box = new THREE.Box3().setFromObject(clone);
    const size = box.getSize(new THREE.Vector3());
    const s = 1.0 / Math.max(size.y, 0.01);
    clone.scale.setScalar(s);
    const center = box.getCenter(new THREE.Vector3());
    clone.position.set(-center.x * s, -box.min.y * s, -center.z * s);
    return clone;
  }
  // Fallback: yellow sphere + cone beak, ~1u tall body.
  const fallback = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.4, 10, 8),
    new THREE.MeshToonMaterial({ color: PALETTE.duck }),
  );
  body.castShadow = true;
  body.position.y = 0.4;
  fallback.add(body);
  const beak = new THREE.Mesh(
    new THREE.ConeGeometry(0.1, 0.18, 6),
    new THREE.MeshToonMaterial({ color: PALETTE.duckBeak }),
  );
  beak.rotation.x = Math.PI / 2;
  beak.position.set(0, 0.45, 0.35);
  fallback.add(beak);
  return fallback;
}

export function createDuckling(world: World, index: number): DucklingEntity {
  loadTemplate(); // safety; usually no-op since module-init already kicked it off.

  const group = new THREE.Group();
  group.name = `duckling-${index}`;
  const usedFallback = !cachedRawTemplate;
  group.add(buildVisual());
  // Scale the wrapper, not the visual — preserves the per-clone normalization.
  group.scale.setScalar(DUCKLING.scale);
  group.position.copy(world.duck.position);
  world.scene.add(group);

  // If we had to use the sphere fallback, swap to the proper GLTF as soon as
  // it loads so the brood doesn't have a mismatched "ball" stuck in the line.
  if (usedFallback) {
    templateReadyCallbacks.push(() => {
      while (group.children.length > 0) group.remove(group.children[0]!);
      group.add(buildVisual());
    });
  }

  return {
    group,
    mode: 'following',
    index,
    assignedCop: null,
    orbitAngle: Math.random() * Math.PI * 2,
    returnUntil: 0,
  };
}
